import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { apiGet } from "../../shared/apiClient";
import { fetchWalletTransactionsByCustomerId } from "../../shared/customerMastersApi";
import { IconList, IconRefresh } from "../../shared/toolbarIcons";
import { mastersDataGridSx } from "../../shared/mastersDataGridSx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

function formatMoney(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function WalletTransactionsPage() {
  const [searchParams] = useSearchParams();
  const customerParam = searchParams.get("customer");
  const customerId = Number(customerParam);

  const [customerMeta, setCustomerMeta] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState(null);

  const customerValid = Boolean(customerParam) && Number.isFinite(customerId) && customerId > 0;

  const reloadTransactions = useCallback(async () => {
    if (!customerValid) return;
    setListLoading(true);
    const res = await fetchWalletTransactionsByCustomerId(customerId);
    setListLoading(false);
    if (!res.ok) {
      setError(res.error ?? "Failed to load transactions");
      setTransactions([]);
      return;
    }
    setError(null);
    setTransactions(res.items);
  }, [customerId, customerValid]);

  useEffect(() => {
    if (!customerValid) return;
    let cancelled = false;
    (async () => {
      const cRes = await apiGet(`/api/Customers/${customerId}`);
      if (cancelled) return;
      if (cRes.ok) setCustomerMeta(cRes.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId, customerValid]);

  useEffect(() => {
    void reloadTransactions();
  }, [reloadTransactions]);

  const rowsWithBalance = useMemo(() => {
    const sorted = [...transactions].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
    let running = 0;
    return sorted.map((t) => {
      running += Number(t.credit ?? 0) - Number(t.debit ?? 0);
      return {
        id: t.id,
        debit: formatMoney(t.debit),
        credit: formatMoney(t.credit),
        balance: formatMoney(running),
        item: t
      };
    });
  }, [transactions]);

  const columns = useMemo(
    () => [
      { field: "debit", headerName: "Debit", width: 130 },
      { field: "credit", headerName: "Credit", width: 130 },
      { field: "balance", headerName: "Balance", width: 130 }
    ],
    []
  );

  const customerLabel = customerMeta?.name ?? `Customer #${customerId}`;

  if (!customerValid) {
    return <Navigate to="/customers-list" replace />;
  }

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Wallet transactions</h2>
          <p>{customerLabel}</p>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void reloadTransactions()}
            disabled={listLoading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/customers-list" className="icon-button icon-button--toolbar" title="Customers list" aria-label="Back to customers list">
            <IconList />
          </Link>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rowsWithBalance}
            columns={columns}
            loading={listLoading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={mastersDataGridSx}
          />
        </div>
      </div>
    </div>
  );
}
